import { postCloudAiStream } from './cloudApi';
import { isCoachablePersonalNameHint } from './anonymizeForAi';

const cleanText = (value) => String(value || '')
  .replace(/<start_of_turn>|<end_of_turn>|<start_of_image>|<\|im_(start|end)\|>|```|\[END\]|\[INST\]|\[\/INST\]/gi, ' ')
  .replace(/\s+/g, ' ')
  .trim();

/** Match [type: value] with type letters/underscore/digits (case-insensitive type). */
const parseCommands = (responseText) => {
  const commandPattern = /\[([\w]+):\s*([^\]]+)\]/g;
  const commands = [];
  const s = String(responseText || '');
  for (const match of s.matchAll(commandPattern)) {
    commands.push({ type: String(match[1] || '').trim().toUpperCase(), value: String(match[2] || '').trim() });
  }
  const text = cleanText(s.replace(commandPattern, ' '));
  return { text, commands };
};

/**
 * Remove any bracket tag that contains "onboard" (case-insensitive), e.g. [onboard done], [ONBOARD_DONE], [ONBOARD_GOAL: …].
 * Used for visible stream text so machine tokens never flash on screen.
 */
export const stripAllOnboardingBracketTags = (raw) => {
  let t = String(raw || '').replace(/\[[^\]]*onboard[^\]]*\]/gi, '');
  t = t.replace(/\[[A-Z_]+:\s*[^\]]+\]/g, '');
  t = t.replace(/\[[\w]*(?::[^\]]*)?$/g, '').trimEnd();
  return cleanText(t);
};

export const stripOnboardingCommandTagsForStream = (raw) => stripAllOnboardingBracketTags(raw);

const sanitizeName = (raw) => {
  const s = String(raw ?? '').trim().slice(0, 40);
  if (!s) return '';
  if (s.startsWith('[')) return '';
  if (s.toLowerCase() === '[name]') return '';
  return s;
};

const goalDedupeKey = (t) => String(t || '').trim().toLowerCase();
export const onboardingGoalDedupeKey = goalDedupeKey;

const isBadOnboardingReply = (value) => {
  const text = String(value || '').trim().toLowerCase();
  if (!text) return true;
  if (text.startsWith('subject:') || text.includes('rebuild-me support')) return true;
  if (/<start_of_turn>|<end_of_turn>|```/i.test(text)) return true;
  if (/\bthe user is\b/.test(text)) return true;
  if (/\byour role is\b/.test(text)) return true;
  return false;
};

/**
 * When the UI already has a suggested first name from sign-in but no settled draft name yet,
 * infer if a short user reply is confirming that suggestion (without handing "yes, and also…"
 * long answers the wrong name).
 */
/** Apple Hide My Email and similar — do not treat as a personal name source. */
const isMaskedOrRelaySignInEmail = (email) => {
  const e = String(email || '').trim().toLowerCase();
  if (!e || !e.includes('@')) return true;
  if (/@privaterelay\.appleid\.com$/i.test(e)) return true;
  if (e.includes('privaterelay')) return true;
  return false;
};

/**
 * First name from Google/Apple when Firebase exposes a real display name or a human-like email local-part.
 * Returns '' when unknown (e.g. masked relay, no display name, or unusable local) so onboarding asks what to call them.
 * @param {{ displayName?: string | null; email?: string | null } | null | undefined} firebaseUser
 */
export const deriveSignInFirstName = (firebaseUser) => {
  if (!firebaseUser) return '';
  const dn = String(firebaseUser.displayName || '').trim();
  if (dn) {
    const first = (dn.split(/\s+/)[0] || '').trim();
    if (first && isCoachablePersonalNameHint(first)) {
      return first.slice(0, 40);
    }
  }
  const email = String(firebaseUser.email || '').trim();
  if (!email || isMaskedOrRelaySignInEmail(email)) return '';
  const localRaw = (email.split('@')[0] || '').split('+')[0].trim();
  if (localRaw.length < 2 || localRaw.length > 40) return '';
  if (/[0-9]{6,}/.test(localRaw)) return '';
  if (!/^[a-zA-Z]/.test(localRaw)) return '';
  const parts = localRaw.split(/[._-]/).filter((p) => /^[a-zA-Z]{2,24}$/.test(p));
  if (!parts.length) return '';
  const candidate = parts[0];
  if (!isCoachablePersonalNameHint(candidate)) return '';
  const norm = candidate.charAt(0).toUpperCase() + candidate.slice(1).toLowerCase();
  return norm.slice(0, 40);
};

export const inferSuggestedNameFromUserReply = (userText, suggestedName, existingDraftName) => {
  if (existingDraftName || !suggestedName) return '';
  const t = String(userText || '').trim();
  if (!t || t.length > 56) return '';

  if (
    /\b(but|however|except|actually|not really|rather|instead|prefer|different|other name|wrong name)\b/i.test(
      t,
    )
  ) {
    return '';
  }
  const wholeWordYes = /^(?:y|yes|yeah|yep|yup|sure|ok|okay|correct|fine|please|absolutely|exactly|perfect)(?:[.!…]+)?$/i.test(
    t,
  );
  const shortPhrasedYes =
    t.length <= 44 &&
    /^(?:y|yes|yeah|yep|yup|sure|ok|okay|correct|fine|please|absolutely|exactly|perfect)\b/i.test(t) &&
    /^(?:y|yes|yeah|yep|yup|sure|ok|okay|correct|fine|please|absolutely|exactly|perfect)(?:\s*[,.]?\s*)?(?:that's\s*(?:fine|good|me|right|perfect)|that works|sounds good|all good|good with me|works for me|love it|go with that|keep it|use that|for me)\.?$/i.test(
      t,
    );

  if (wholeWordYes || shortPhrasedYes) {
    return String(suggestedName).trim().slice(0, 40);
  }
  return '';
};

/** First model turn on Home: no user message yet; model streams the opening in the main chat. */
export const buildOnboardingBootstrapUserMessage = (suggestedFirstName) => {
  const s = sanitizeName(suggestedFirstName);
  if (s) {
    return `[Suggested first name from sign-in: ${s}] (System: no user reply yet—send ONLY your opening onboarding message. Follow the opening-turn rules. Use this name directly; include [ONBOARD_SET_NAME: ${s}] on its own line in that message. Do not ask if another name is OK.)`;
  }
  return `(System: no user reply yet—send ONLY your opening onboarding message. Follow the opening-turn rules.)`;
};

const finalizeOnboardingStreamResult = (rawModelText, usageEstimateFromServer, onStreamDelta) => {
  let usageEstimate = Number(usageEstimateFromServer) || 0;
  if (!usageEstimate) {
    usageEstimate = Math.ceil((String(rawModelText || '').length) / 3.5);
  }

  const { text: parsedText, commands } = parseCommands(cleanText(rawModelText));
  const cleanedText = cleanText(parsedText);

  if (isBadOnboardingReply(cleanedText)) {
    const fb = 'Thanks for that. Tell me in a few words what you would like to focus on first, or say skip if you prefer to set goals later in Settings.';
    onStreamDelta?.(fb);
    return { text: fb, commands: [], rawModelText: '', usageEstimate: 0 };
  }

  return {
    text: cleanedText,
    commands,
    rawModelText: cleanText(rawModelText),
    usageEstimate,
  };
};

const streamOnboardingFromServer = async ({ requestBody, onStreamDelta, signal }) => {
  let rawModelText = '';
  let usageEstimate = 0;

  await postCloudAiStream(
    '/api/ai/onboarding-stream',
    {
      ...requestBody,
      max_tokens: requestBody.max_tokens ?? 320,
      temperature: requestBody.temperature ?? 0.5,
    },
    {
      signal,
      onDelta: (d) => {
        rawModelText += d;
        const vis = stripOnboardingCommandTagsForStream(rawModelText);
        // While the model streams bracket-only lines first, visible text can be empty—keep a
        // placeholder so Home does not treat the row as an "orphan" and so the UI is not blank.
        onStreamDelta?.(vis.trim() ? vis : rawModelText.trim() ? '…' : '');
      },
      onDone: (payload) => {
        usageEstimate = Number(payload?.usageEstimate) || 0;
      },
      onError: (err) => {
        throw err;
      },
    },
  );

  return finalizeOnboardingStreamResult(rawModelText, usageEstimate, onStreamDelta);
};

/** After settings are saved: stream the Google-link invitation in the same chat thread (if Google is not linked). */
export const generateOnboardingPostSaveStreamReply = async ({
  sessionMessages,
  variant,
  onStreamDelta,
  signal,
}) => {
  try {
    const v = String(variant || '').toLowerCase();
    return await streamOnboardingFromServer({
      requestBody: {
        sessionMessages,
        postSaveVariant: variant,
        temperature: v === 'google' ? 0.2 : undefined,
      },
      onStreamDelta,
      signal,
    });
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    console.error('Onboarding post-save stream error:', msg);
    const err = 'Something went wrong on my side. Please try again in a moment.';
    onStreamDelta?.(err);
    return { text: err, commands: [], rawModelText: '', usageEstimate: 0 };
  }
};

/** Only treat explicit machine tags as "done" — loose patterns matched normal prose and broke onboarding. */
const detectLooseOnboardingDone = (raw) => {
  const s = String(raw || '');
  return (
    /\[(?:ONBOARD_DONE|ONBOARD_COMPLETE|ONBOARD_FINISHED)\]/i.test(s)
    || /\[\s*ONBOARD[\s_]*:[\s]*(?:DONE|COMPLETE|FINISHED)\s*\]/i.test(s)
  );
};

/**
 * Parse ONBOARD_* commands from a completed assistant message (include raw model text so loose tags are detected).
 */
export const parseOnboardingPatches = (rawAssistantText) => {
  const raw = String(rawAssistantText || '');
  const looseDone = detectLooseOnboardingDone(raw);
  const { text, commands } = parseCommands(cleanText(raw));

  const goalMap = new Map();
  const deleteGoals = [];
  let name;
  let done = looseDone;

  const applyGoal = (primaryRaw) => {
    const primary = String(primaryRaw || '').trim().slice(0, 200);
    if (!primary) return;
    goalMap.set(goalDedupeKey(primary), { text: primary });
  };

  for (const cmd of commands) {
    const type = String(cmd?.type || '').toUpperCase().replace(/\s+/g, '_');
    const value = String(cmd?.value || '').trim();
    if (!type) continue;

    if (type === 'ONBOARD_SET_NAME') {
      const n = sanitizeName(value);
      if (n) name = n;
      continue;
    }

    if (type === 'ONBOARD_GOAL') {
      const pipe = value.indexOf('|');
      const primary = pipe >= 0 ? value.slice(0, pipe) : value;
      applyGoal(primary);
      continue;
    }

    if (type === 'ONBOARD_DELETE_GOAL') {
      const needle = String(value || '').trim();
      if (needle.length >= 2) deleteGoals.push(needle);
      continue;
    }

    if (
      type === 'ONBOARD_DONE'
      || type === 'ONBOARD_COMPLETE'
      || type === 'ONBOARD:DONE'
      || (type === 'ONBOARD' && /^(DONE|COMPLETE|FINISHED)$/i.test(value))
    ) {
      done = true;
    }
  }

  const goals = Array.from(goalMap.values()).slice(0, 5);
  const patches = { goals, deleteGoals };
  if (name) patches.name = name;
  return { patches, done, cleanedAssistant: stripAllOnboardingBracketTags(cleanText(text)) };
};

export const mergeDedupedGoalsIntoDraft = (existingGoals, patchGoalsFromMessage) => {
  const map = new Map();
  for (const g of existingGoals || []) {
    const k = goalDedupeKey(g?.text);
    if (k) map.set(k, { text: String(g.text || '').trim() });
  }
  for (const g of patchGoalsFromMessage || []) {
    const k = goalDedupeKey(g?.text);
    if (k) map.set(k, { text: String(g.text || '').trim() });
  }
  return Array.from(map.values()).slice(0, 5);
};

export const generateOnboardingChatReply = async ({
  sessionMessages,
  userMessage,
  onStreamDelta,
  signal,
}) => {
  try {
    return await streamOnboardingFromServer({
      requestBody: { sessionMessages, userMessage },
      onStreamDelta,
      signal,
    });
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    console.error('Onboarding chat error:', msg);
    const err = 'Something went wrong on my side. Please try sending that again in a short line.';
    onStreamDelta?.(err);
    return { text: err, commands: [], rawModelText: '', usageEstimate: 0 };
  }
};

// Re-parse patches from full raw + command list (single source of truth).
export const parseOnboardingResult = (result) => {
  const raw = `${result?.rawModelText || result?.text || ''}\n${(result?.commands || [])
    .map((c) => `[${c.type}: ${c.value}]`)
    .join('\n')}`;
  return parseOnboardingPatches(raw);
};
